import { selectIsPremium } from "domain/auth/auth.selectors";
import { HiHeart, HiLockClosed, HiOutlineHeart, HiPlay } from "react-icons/hi";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { MouseEvent } from "react";
import { selectFavoriteTutorials } from "domain/activities/activities.selectors";
import { toggleActivityFavorite } from "ui/utils/toggle-activity-favorite";
import { Tutorial, TutorialType } from "domain/entities/tutorials.entities";
import { useWindowWidth } from "ui/utils/hooks";
import { CardBgPink200 } from "ui/assets/svg/tutorial-card-backgrounds/CardBgPink200";
import { CardBgDarkGrey800 } from "ui/assets/svg/tutorial-card-backgrounds/CardBgDarkGrey800";
import { CardBgDarkGrey200 } from "ui/assets/svg/tutorial-card-backgrounds/CardBgDarkGrey200";
import { CardBgPink50 } from "ui/assets/svg/tutorial-card-backgrounds/CardBgPink50";

interface IProps {
  tutorial: Tutorial;
  onClick: () => void;
  compact?: boolean;
}

export function TutorialCard({ tutorial, onClick, compact }: IProps) {
  const isPremium = useAppSelector(selectIsPremium);
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const CardImage = getCardImage(tutorial.series as TutorialType);
  const favoriteTutorial = useAppSelector(selectFavoriteTutorials).find(
    (t) => t.tutorialId === tutorial?.id
  );

  const handleCardClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (onClick) onClick();
  };

  const handleFavoriteClick = async (e: MouseEvent) => {
    e.stopPropagation();
    if (!isPremium && !!tutorial.premium) {
      if (onClick) onClick();
    } else {
      toggleActivityFavorite({
        favoriteActivity: favoriteTutorial,
        dispatch,
        activityId: tutorial.id,
        activityType: "tutorial",
      });
    }
  };

  return (
    <div
      className={`flex h-full cursor-pointer flex-col justify-between rounded-[20px] bg-white p-3 ${
        onClick ? "cursor-pointer" : ""
      } ${compact ? "min-h-[270px]" : "md:p-5"}`}
      onClick={handleCardClick}
    >
      <div>
        <div className="relative">
          <div
            className={
              (!isPremium && !!tutorial.premium) || !tutorial.active
                ? "opacity-30"
                : undefined
            }
          >
            <CardImage width="100%" height="100%" />
          </div>

          <button
            disabled={!tutorial.active}
            className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white disabled:opacity-40 ${
              compact ? "" : "md:right-4 md:top-4"
            }`}
            onClick={handleFavoriteClick}
          >
            {!isPremium && !!tutorial.premium ? (
              <HiLockClosed className="text-secondaryGrey-900" size={18} />
            ) : (
              <>
                {!!favoriteTutorial?.id ? (
                  <HiHeart className="text-secondaryGrey-900" size={18} />
                ) : (
                  <HiOutlineHeart
                    className="text-secondaryGrey-900"
                    size={18}
                  />
                )}
              </>
            )}
          </button>

          <div className="absolute-centered">
            <HiPlay className="text-white" size={isDesktop ? 64 : 40} />
          </div>
        </div>

        <span
          className={`mt-2 block text-sm font-bold md:text-base ${
            !isPremium && !!tutorial.premium && "opacity-30"
          }`}
        >
          {tutorial.title}
        </span>

        {!compact && (
          <span
            className={`mt-2 !line-clamp-2 block text-sm ${
              !isPremium && !!tutorial.premium && "opacity-30"
            }`}
          >
            {tutorial.content}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center space-x-2 md:mt-8">
        <span
          className={`rounded-[36px] bg-darkGrey-200 px-3 py-1 text-xs font-semibold ${
            !isPremium && !!tutorial.premium && "opacity-30"
          }`}
        >
          {translate(`ns1:Tutorials.Types.${tutorial.series as TutorialType}`)}
        </span>
      </div>
    </div>
  );
}

function getCardImage(tutorialType: TutorialType) {
  switch (tutorialType) {
    case "NUM":
      return CardBgPink50;
    case "TECH":
      return CardBgDarkGrey800;
    case "SKILLS":
      return CardBgDarkGrey200;
    case "FOND":
      return CardBgPink200;
    default:
      return CardBgPink200;
  }
}
